import { SPOOL_STATUS_LABELS, SpoolStatus } from '@deizi/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { EmptyState, ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { useSpools } from '../../lib/inventory-api';
import { GramsBar, SpoolBadge, Swatch, spoolPlace } from './spool-common';

/** "Carretéis" tab (board Filamentos-Carreteis). */
export function SpoolsTab() {
  const [status, setStatus] = useState<SpoolStatus | ''>('');
  const all = useSpools();
  const toast = useToast();
  const navigate = useNavigate();
  const [picked, setPicked] = useState<string[]>([]);
  if (all.isPending) return <Skeleton rows={4} height={48} />;
  if (all.isError) {
    return (
      <ErrorBox title="Não foi possível carregar os carretéis" onRetry={() => void all.refetch()} />
    );
  }
  const spools = all.data.data.toSorted((a, b) => a.code.localeCompare(b.code));
  if (spools.length === 0) {
    return (
      <section className="card">
        <EmptyState
          title="Nenhum carretel ainda"
          action={
            <Link className="btn p" to="/filamentos/compra">
              Registrar compra
            </Link>
          }
        >
          Registre uma compra e os carretéis ganham código e etiqueta.
        </EmptyState>
      </section>
    );
  }
  const shown = spools.filter((s) => !status || s.status === status);
  const count = (st: SpoolStatus) => spools.filter((s) => s.status === st).length;
  const allPicked = shown.length > 0 && shown.every((s) => picked.includes(s.id));
  const cols = '34px 90px 2fr 1.4fr 1.4fr 1.4fr';
  return (
    <section className="card flat">
      <div className="row wrap" style={{ gap: 8, padding: '14px 16px' }}>
        <button
          type="button"
          className={status === '' ? 'chip on' : 'chip'}
          onClick={() => setStatus('')}
        >
          Todos · {spools.length}
        </button>
        {SpoolStatus.options.map((st) => (
          <button
            key={st}
            type="button"
            className={status === st ? 'chip on' : 'chip'}
            onClick={() => setStatus(st)}
          >
            {SPOOL_STATUS_LABELS[st]} · {count(st)}
          </button>
        ))}
        <div className="grow" />
        <span className="sm muted">
          {picked.length
            ? `${picked.length} selecionado${picked.length > 1 ? 's' : ''}`
            : 'Marque os carretéis'}
        </span>
        <button
          type="button"
          className="btn sm"
          onClick={() => {
            if (!picked.length) {
              toast('Marque ao menos um carretel.');
              return;
            }
            void navigate(`/carreteis/etiquetas?ids=${picked.join(',')}`);
          }}
        >
          Imprimir etiquetas
        </button>
      </div>
      <div className="gt" style={{ overflowX: 'auto' }}>
        <div className="gtr head" style={{ gridTemplateColumns: cols, minWidth: 720 }}>
          <span>
            <input
              type="checkbox"
              aria-label="Selecionar todos"
              checked={allPicked}
              onChange={() =>
                setPicked(allPicked ? [] : [...new Set([...picked, ...shown.map((s) => s.id)])])
              }
            />
          </span>
          <span>Código</span>
          <span>Filamento</span>
          <span>Situação</span>
          <span className="num">Restante</span>
          <span>Local</span>
        </div>
        {shown.map((s) => (
          <div key={s.id} className="gtr" style={{ gridTemplateColumns: cols, minWidth: 720 }}>
            <span>
              <input
                type="checkbox"
                aria-label={`Selecionar ${s.code}`}
                checked={picked.includes(s.id)}
                onChange={() =>
                  setPicked(
                    picked.includes(s.id) ? picked.filter((x) => x !== s.id) : [...picked, s.id],
                  )
                }
              />
            </span>
            <Link to={`/carreteis/${s.id}`} className="tab" style={{ fontWeight: 600 }}>
              {s.code}
            </Link>
            <span className="row" style={{ gap: 8, minWidth: 0 }}>
              <Swatch hex={s.filament.colorHex} />
              <span className="trunc">{s.filament.name}</span>
            </span>
            <span>
              <SpoolBadge spool={s} />
            </span>
            <GramsBar spool={s} />
            <span className="sm muted trunc">{spoolPlace(s)}</span>
          </div>
        ))}
        {shown.length === 0 ? (
          <div className="empty" style={{ padding: 24 }}>
            <p>Nenhum carretel nessa situação.</p>
            <button type="button" className="btn sm" onClick={() => setStatus('')}>
              Limpar filtros
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
